/*
 * Copyright (c) 2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {Fragment, useEffect, useRef, useState} from "react";
import CompanyFormLabel from "pages/Front/Booking/components/CompanyForm/CompanyFormLabel";
import {Listbox} from "@headlessui/react";
import clsx from "clsx";
import {CheckIcon, ChevronDownIcon} from "@heroicons/react/24/solid";
import {useTranslation} from "react-i18next";
import {ExclamationCircleIcon} from "@heroicons/react/24/outline";
import T from "components/T";
import {Overlay} from "react-overlays";

export interface CompanyFormDropdownOption {
    id: string;
    name: string;
}

interface CompanyFormDropdownProps {
    options: CompanyFormDropdownOption[];
    defaultValue: string | null;
    register: any;
    name: string;
    label: string;
    className?: string;
    classNameInput?: string;
    classNameLabel?: string;
    required?: boolean;
    errors?: any;
    disabled?: boolean;
    onFocus?: (element: any) => void;
    setSelected: (country: string) => void;
}

const CompanyFormDropdown = (props: CompanyFormDropdownProps) => {
    const {t} = useTranslation();
    const [selected, setSelected] = useState<CompanyFormDropdownOption | null>(null);
    const [show, setShow] = useState<boolean>(false);
    const selectedOptionRef = useRef<HTMLLIElement>(null);
    const targetRef = useRef(null);
    const options = props?.options;

    const setSelectedHandler = (country: CompanyFormDropdownOption) => {
        setSelected(country);
        setShow(false);
        props.setSelected(country.id);
    };

    useEffect(() => {
        setSelected(props.defaultValue ? props.options.find(option => option.id == props.defaultValue) || null : null);

        console.log(props.options, props.defaultValue);
    }, [props.defaultValue]);

    return (
        <div className={clsx("CompanyFormDropdown w-full relative", props.className)}>
            <CompanyFormLabel className={props.classNameLabel} errors={props.errors} label={props.label} name={props.name} required={props.required} />
            <div className={"flex flex-row relative w-full"}>
                <Listbox disabled={props.disabled} value={selected} onChange={setSelectedHandler}>
                    {({open}) => (
                        <div className="w-full">
                            <Listbox.Button
                                ref={targetRef}
                                className={clsx(
                                    "relative cursor-pointer rounded-md bg-white border border-gray-800 text-sm text-left text-gray-900 shadow-sm focus:outline-none active:ring-cta-800 active:ring-2 active:ring-offset-2 focus:border-cta-800 checked:bg-cta-800 sm:text-sm",
                                    "px-3 py-3 w-full h-full flex items-center justify-between",
                                    props.disabled
                                        ? selected
                                            ? "bg-gray-100 text-gray-600 pointer-events-none"
                                            : "bg-gray-100 text-gray-400"
                                        : selected
                                          ? "bg-white text-black"
                                          : "bg-white text-gray-400",
                                    selected ? "text-black border-black" : "text-gray-400",
                                    props.errors?.[props.name] && !open && !selected && "border-red-500"
                                )}
                                onClick={() => setShow(!show)}
                            >
                                <span className={!selected?.name ? "text-gray-500" : ""}>{selected?.name || t("Select")}</span>
                                <span className="pointer-events-none pl-2">
                                    <ChevronDownIcon className={"w-3.5 h-3.5"} />
                                </span>
                            </Listbox.Button>
                            <Overlay
                                placement="bottom-start"
                                show={show}
                                target={targetRef.current}
                                rootClose
                                onHide={() => setShow(false)}
                            >
                                {({ props, placement }) => (
                                    <Listbox.Options
                                        {...props}
                                        style={{
                                            ...props.style,
                                            transition: "maxHeight 0.3s ease-in-out",
                                            // @ts-ignore
                                            width: targetRef?.current ? targetRef.current.getBoundingClientRect().width : "100%",
                                        }}
                                        className={clsx(
                                            "transition-all duration-300 ease-in h-full drop-shadow shadow-sm z-50 max-h-[15rem] w-full max-w-full rounded-md bg-white overflow-x-hidden py-1 text-base",
                                            placement
                                        )}
                                        unmount={false}
                                        onLoad={() => {
                                            if (selected && selectedOptionRef?.current) {
                                                selectedOptionRef.current.scrollIntoView({
                                                    behavior: "smooth",
                                                    block: "nearest",
                                                });
                                            }
                                        }}
                                    >
                                        {options.map(option => (
                                            <Listbox.Option key={option.id} as={Fragment} value={option}>
                                                {({selected, active}) => (
                                                    <li
                                                        ref={selected ? selectedOptionRef : null}
                                                        className={clsx(
                                                            "relative flex flex-row cursor-pointer select-none py-2 pr-0 text-sm",
                                                            active || selected ? "bg-gray-200/80 text-black" : ""
                                                        )}
                                                    >
                                                        {selected && (
                                                            <span className={"w-9 flex items-center"}>
                                                                <CheckIcon className={"w-[18px] h-[18px] grow ml-0"} />
                                                            </span>
                                                        )}
                                                        <div className={clsx("flex items-center truncate", !selected && "pl-9")}>
                                                            <span className={clsx("ml-1 block truncate")}>{option.name}</span>
                                                        </div>
                                                    </li>
                                                )}
                                            </Listbox.Option>
                                        ))}
                                    </Listbox.Options>
                                )}
                            </Overlay>
                        </div>
                    )}
                </Listbox>
            </div>

            {props.errors?.[props.name]?.message && (
                <div className={clsx("text-red-500 text-sm mt-1 flex flex-row items-center shrink-0")}>
                    <div className={clsx("shrink-0 flex flex-row items-center w-full")}>
                        <ExclamationCircleIcon className={"min-w-[18px] min-h-[18px] w-[18px] h-[18px] mr-0.5"} />
                        <T>{props.errors?.[props.name]?.message}</T>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CompanyFormDropdown;
